import { useEffect, useLayoutEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { MarketingLang, splitLangPath, toMarketingLang, withLang } from "@/lib/langRoutes";

/**
 * Mounted on the /en and /fr route trees: the URL is the source of truth for
 * the language, so the i18n state is aligned to the prefix (not the reverse).
 */
export const LangRoot = ({ lang }: { lang: MarketingLang }) => {
  const { i18n: inst } = useTranslation();
  useEffect(() => {
    if ((inst.language || "").split("-")[0] !== lang) inst.changeLanguage(lang);
  }, [lang, inst]);
  return <Outlet />;
};

/**
 * Mounted on the un-prefixed (Arabic) marketing tree. If the visitor is
 * browsing in English/French, send them to the canonical prefixed URL so the
 * URL and the rendered language never disagree.
 */
export const DefaultLangRoot = () => {
  const { i18n: inst } = useTranslation();

  // The URL is the source of truth: the un-prefixed tree is the Arabic version,
  // so an English/French UI state is switched back to Arabic here. We never
  // redirect away from "/" (a crawler with an English locale must be able to
  // reach and index the canonical Arabic page).
  useEffect(() => {
    const current = (inst.language || "").split("-")[0];
    if (current === "en" || current === "fr") inst.changeLanguage("ar");
  }, [inst, inst.language]);

  return <Outlet />;
};

/**
 * Legacy query-parameter language URLs (/?lng=en, /pricing?lng=fr, ...) are
 * redirected to the permanent prefixed routes.
 */
export const LangQueryRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Layout effect: the hop happens before paint, so the legacy URL never
  // renders its own frame (single redirect step, no chain).
  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("lng");
    if (!raw) return;
    const lang = toMarketingLang(raw);
    params.delete("lng");
    const { basePath } = splitLangPath(location.pathname);
    const qs = params.toString();
    if ((i18n.language || "").split("-")[0] !== lang) i18n.changeLanguage(lang);
    navigate(`${withLang(basePath, lang)}${qs ? `?${qs}` : ""}${location.hash}`, {
      replace: true,
    });
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

/** Prefix a language-neutral path with the language segment of the current URL. */
export function useLocalePath() {
  const { pathname } = useLocation();
  const { lang } = splitLangPath(pathname);
  return (basePath: string) => withLang(basePath, lang);
}
